// admin/src/pages/Dashboard/components/KpiCardsSection.js
import React from "react";
import { Grid, Typography, Box, Chip, Skeleton } from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import AppsIcon from "@mui/icons-material/Apps";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import Sparkline from "./Sparkline";
import { formatNumber, formatPercent } from "../dashboardConstants";

/**
 * Calculates percentage change between current and previous values.
 * Returns neutral dash when current is null or undefined.
 */
const calculateDelta = (current, prev) => {
  if (current === null || current === undefined) {
    return { text: "—", isNeutral: true, isPositive: true };
  }
  if (prev === null || prev === undefined || prev === 0) {
    if (current > 0) return { text: "+100%", isPositive: true, isNeutral: false };
    return { text: "0.0%", isPositive: true, isNeutral: false };
  }
  const delta = ((current - prev) / prev) * 100;
  const sign = delta > 0 ? "+" : "";
  return {
    text: `${sign}${delta.toFixed(1)}%`,
    isPositive: delta >= 0,
    isNeutral: false,
  };
};

const KpiCardWrapper = ({ children, T, delay = 1 }) => (
  <div
    className={`dl-card dl-f dl-f${delay}`}
    style={{
      minHeight: 160,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "18px 20px",
    }}
  >
    {children}
  </div>
);

const KpiCardsSection = ({ kpis, loading = false, T }) => {
  if (loading || !kpis) {
    return (
      <Grid container spacing={2} mb={2.5}>
        {[1, 2, 3, 4, 5, 6].map((idx) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
            <div className="dl-card" style={{ minHeight: 160, padding: "18px 20px" }}>
              <Box display="flex" justifyContent="space-between" mb={1.5}>
                <Skeleton variant="circular" width={38} height={38} />
                <Skeleton variant="rounded" width={54} height={22} />
              </Box>
              <Skeleton variant="text" width="60%" height={36} />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="text" width="50%" height={16} />
            </div>
          </Grid>
        ))}
      </Grid>
    );
  }

  const signIns = kpis.signIns || {};
  const prevSignIns = signIns.prev || {};
  const signInDelta = calculateDelta(signIns.total, prevSignIns.total);

  const mfaRateDelta = calculateDelta(kpis.mfaSuccessRate, prevSignIns.mfaSuccessRate ?? prevSignIns.successRate);

  const activeUsers = kpis.activeUsers || { active: 0, total: 0 };
  const activePct = activeUsers.total > 0 ? (activeUsers.active / activeUsers.total) * 100 : 0;

  const mfaCoverage = kpis.mfaCoverage || { percent: 0, usersWithAnyMfa: 0, total: 0 };
  const devices = kpis.devices || { usable: 0, blocked: 0, revoked: 0, stale30d: 0 };
  const apps = kpis.applications || { total: 0, oidc: 0, saml: 0, mfaOff: 0 };

  return (
    <Grid container spacing={2} mb={2.5}>
      {/* 1. Sign-ins */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiCardWrapper T={T} delay={1}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${T.accent1}14`,
                border: `1px solid ${T.accent1}25`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginIcon sx={{ fontSize: 20, color: T.accent1 }} />
            </div>
            <Chip
              size="small"
              icon={
                signInDelta.isNeutral ? undefined : signInDelta.isPositive ? (
                  <TrendingUpIcon sx={{ fontSize: "14px !important" }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: "14px !important" }} />
                )
              }
              label={signInDelta.text}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background: signInDelta.isNeutral
                  ? `${T.textMuted}18`
                  : signInDelta.isPositive
                  ? `${T.accent2}18`
                  : `${T.accent3}18`,
                color: signInDelta.isNeutral ? T.textMuted : signInDelta.isPositive ? T.accent2 : T.accent3,
                border: `1px solid ${
                  signInDelta.isNeutral
                    ? `${T.textMuted}30`
                    : signInDelta.isPositive
                    ? `${T.accent2}30`
                    : `${T.accent3}30`
                }`,
              }}
            />
          </Box>
          <Box display="flex" alignItems="baseline" justifyContent="space-between">
            <Typography
              sx={{
                fontFamily: T.fontHeading,
                fontSize: 26,
                fontWeight: 800,
                color: T.textPrimary,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              {formatNumber(signIns.total)}
            </Typography>
            {signIns.sparkline && (
              <Box sx={{ ml: 1, flexShrink: 0 }}>
                <Sparkline data={signIns.sparkline} color={T.accent1} width={64} height={26} />
              </Box>
            )}
          </Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.textSecondary, mt: 0.5 }}>
            Sign-ins
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
            {signIns.successRate !== null && signIns.successRate !== undefined
              ? `${formatPercent(signIns.successRate)} success rate`
              : "— · No data in this period"}
          </Typography>
        </KpiCardWrapper>
      </Grid>

      {/* 2. MFA Success Rate */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiCardWrapper T={T} delay={2}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${T.accent2}14`,
                border: `1px solid ${T.accent2}25`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: T.accent2 }} />
            </div>
            <Chip
              size="small"
              icon={
                mfaRateDelta.isNeutral ? undefined : mfaRateDelta.isPositive ? (
                  <TrendingUpIcon sx={{ fontSize: "14px !important" }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: "14px !important" }} />
                )
              }
              label={mfaRateDelta.text}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background: mfaRateDelta.isNeutral
                  ? `${T.textMuted}18`
                  : mfaRateDelta.isPositive
                  ? `${T.accent2}18`
                  : `${T.accent3}18`,
                color: mfaRateDelta.isNeutral ? T.textMuted : mfaRateDelta.isPositive ? T.accent2 : T.accent3,
                border: `1px solid ${
                  mfaRateDelta.isNeutral
                    ? `${T.textMuted}30`
                    : mfaRateDelta.isPositive
                    ? `${T.accent2}30`
                    : `${T.accent3}30`
                }`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: T.fontHeading,
              fontSize: 26,
              fontWeight: 800,
              color: T.textPrimary,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {kpis.mfaSuccessRate !== null && kpis.mfaSuccessRate !== undefined
              ? formatPercent(kpis.mfaSuccessRate)
              : "—"}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.textSecondary, mt: 0.5 }}>
            MFA Success Rate
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
            {kpis.mfaSuccessRate !== null && kpis.mfaSuccessRate !== undefined
              ? "Challenge verifications"
              : "No data in this period"}
          </Typography>
        </KpiCardWrapper>
      </Grid>

      {/* 3. Active Users */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiCardWrapper T={T} delay={3}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${T.accent4}14`,
                border: `1px solid ${T.accent4}25`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PeopleAltOutlinedIcon sx={{ fontSize: 20, color: T.accent4 }} />
            </div>
            <Chip
              size="small"
              label={`${Math.round(activePct)}% active`}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background: `${T.accent4}18`,
                color: T.accent4,
                border: `1px solid ${T.accent4}30`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: T.fontHeading,
              fontSize: 26,
              fontWeight: 800,
              color: T.textPrimary,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {formatNumber(activeUsers.active)}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.textSecondary, mt: 0.5 }}>
            Active Users
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
            of {formatNumber(activeUsers.total)} total users
          </Typography>
        </KpiCardWrapper>
      </Grid>

      {/* 4. MFA Coverage */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiCardWrapper T={T} delay={4}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${T.accent2}14`,
                border: `1px solid ${T.accent2}25`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SecurityOutlinedIcon sx={{ fontSize: 20, color: T.accent2 }} />
            </div>
            <Chip
              size="small"
              label={
                mfaCoverage.percent === null || mfaCoverage.percent === undefined
                  ? "—"
                  : mfaCoverage.percent >= 80
                  ? "Good"
                  : mfaCoverage.percent >= 50
                  ? "Moderate"
                  : "Low"
              }
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background:
                  mfaCoverage.percent === null || mfaCoverage.percent === undefined
                    ? `${T.textMuted}18`
                    : mfaCoverage.percent >= 80
                    ? `${T.accent2}18`
                    : mfaCoverage.percent >= 50
                    ? `${T.accent5}18`
                    : `${T.accent3}18`,
                color:
                  mfaCoverage.percent === null || mfaCoverage.percent === undefined
                    ? T.textMuted
                    : mfaCoverage.percent >= 80
                    ? T.accent2
                    : mfaCoverage.percent >= 50
                    ? T.accent5
                    : T.accent3,
                border: `1px solid ${
                  mfaCoverage.percent === null || mfaCoverage.percent === undefined
                    ? `${T.textMuted}30`
                    : mfaCoverage.percent >= 80
                    ? `${T.accent2}30`
                    : mfaCoverage.percent >= 50
                    ? `${T.accent5}30`
                    : `${T.accent3}30`
                }`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: T.fontHeading,
              fontSize: 26,
              fontWeight: 800,
              color: T.textPrimary,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {formatPercent(mfaCoverage.percent)}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.textSecondary, mt: 0.5 }}>
            MFA Coverage
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
            {formatNumber(mfaCoverage.usersWithAnyMfa)} of {formatNumber(mfaCoverage.total)} users
          </Typography>
        </KpiCardWrapper>
      </Grid>

      {/* 5. Trusted Devices */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiCardWrapper T={T} delay={5}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${T.accent1}14`,
                border: `1px solid ${T.accent1}25`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DevicesOutlinedIcon sx={{ fontSize: 20, color: T.accent1 }} />
            </div>
            <Chip
              size="small"
              label={`${formatNumber(devices.stale30d)} stale`}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background: `${T.accent5}18`,
                color: T.accent5,
                border: `1px solid ${T.accent5}30`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: T.fontHeading,
              fontSize: 26,
              fontWeight: 800,
              color: T.textPrimary,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {formatNumber(devices.usable)}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.textSecondary, mt: 0.5 }}>
            Trusted Devices
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
            {formatNumber(devices.blocked)} blocked · {formatNumber(devices.revoked)} revoked
          </Typography>
        </KpiCardWrapper>
      </Grid>

      {/* 6. Applications */}
      <Grid item xs={12} sm={6} md={4} lg={2}>
        <KpiCardWrapper T={T} delay={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${T.accent6}14`,
                border: `1px solid ${T.accent6}25`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AppsIcon sx={{ fontSize: 20, color: T.accent6 }} />
            </div>
            <Chip
              size="small"
              label={`${formatNumber(apps.mfaOff)} MFA off`}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background: apps.mfaOff > 0 ? `${T.accent5}18` : `${T.accent2}18`,
                color: apps.mfaOff > 0 ? T.accent5 : T.accent2,
                border: `1px solid ${apps.mfaOff > 0 ? T.accent5 : T.accent2}30`,
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: T.fontHeading,
              fontSize: 26,
              fontWeight: 800,
              color: T.textPrimary,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            {formatNumber(apps.total)}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, fontWeight: 600, color: T.textSecondary, mt: 0.5 }}>
            Applications
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
            {formatNumber(apps.oidc)} OIDC · {formatNumber(apps.saml)} SAML
          </Typography>
        </KpiCardWrapper>
      </Grid>
    </Grid>
  );
};

export default React.memo(KpiCardsSection);
